import Module from '../classes/Module';
import { DropdownManager } from '../classes/Dropdown';
import { MenuManager } from '../classes/Menu';
import { app, navigation, search, menu } from '../utils/store';
import { media } from '../utils/media';

export class Navigation extends Module {
    constructor() {
        super({
            element: '[data-navigation]',
            elements: {
                logo: document.querySelector('#brand-logo'),
                actions: document.querySelectorAll('.actions'),
                search: '[data-search]',
                language: '#select-language',
                menu: '[data-navigation-menu]',
                mega: '[data-menu-mega]'
            }
        })
    }

    create() {
        super.create()

        app.nav = this.element
        app.root.style.setProperty('--window-height', `${window.innerHeight}px`)
        app.root.style.setProperty('--window-width', `${window.innerWidth}px`)
        app.root.style.setProperty('--navigation-height', `${app.nav.clientHeight}px`)
        app.root.style.setProperty('--brand-logo-height', `${this.elements.logo.clientHeight}px`)
        app.root.style.setProperty('--brand-logo-width', `${this.elements.logo.clientWidth}px`)

        if(app.nav.querySelector('.navbar-menu')) {
            app.root.style.setProperty('--navbar-menu-height', `${app.nav.querySelector('.navbar-menu').clientHeight}px`)
        }

        if(app.nav.querySelector('.statusbar')) {
            app.root.style.setProperty('--statusbar-height', `${app.nav.querySelector('.statusbar').clientHeight}px`)
        }

        app.root.style.setProperty('--viewport-height', `${window.visualViewport.height}px`)

        if(this.elements.menu) { this.createMenu() }
        if(this.elements.mega) { this.createMega() }
        if(this.elements.language) { this.createLanguage() }
        if(this.elements.actions) { this.createActions() }

        this.defaultNavMode = app.nav.getAttribute('mode') || 'dark'
        this.setColor()
    }

    getScrollTop() {
        return app.viewport === window ? window.scrollY : app.viewport.scrollTop
    }

    getHero() {
        const first = app.wrapper?.firstElementChild

        return first?.classList.contains('hero') ? first : null
    }

    setColor() {
        if (!app.nav || navigation.isOpen || menu.isOpen) {
            return
        }

        // Scrolled → light; at top with hero → dark; otherwise page default.
        if (this.getScrollTop() >= app.nav.clientHeight) {
            app.nav.setAttribute('mode', 'light')
            return
        }

        app.nav.setAttribute('mode', this.getHero() ? 'dark' : this.defaultNavMode)
    }

    closeMenuDropdowns() {
        if (this.elements.menu) {
            DropdownManager.closeAllInMenu(this.elements.menu)
        }
    }

    closeNavigation() {
        if (!navigation.isOpen) {
            return
        }

        this.closeMenuDropdowns()
        app.body.classList.remove('navigation--open')
        navigation.isOpen = false
        navigation.current = null
        search.isActive = false
        search.current = null
        this.setColor()
    }

    createMenu() {
        const toggler = this.element.querySelector('[data-menu-toggler]')

        if(toggler){
            toggler.onclick = (event) => {
                event.preventDefault()
                event.stopPropagation()

                if(navigation.isOpen){
                    MenuManager.closeAll()
                    this.closeNavigation()
                } else {
                    MenuManager.closeAll()
                    app.body.classList.add('navigation--open')
                    app.nav.setAttribute('mode', 'dark')
                    navigation.isOpen = true
                    navigation.current = this.elements.menu
                }
            }
        }

        this.syncQuickMenuPlacement(this.navigationMovableEntries())
    }

    navigationMovableEntries() {
        return {
            afterAnchor: [
                this.element.querySelector('.quick-menu'),
            ],
            append: [
                this.element.querySelector('#account'),
                this.element.querySelector('#select-language'),
            ],
        }
    }

    syncQuickMenuPlacement(groups) {
        const main = this.element.querySelector('[data-navigation-main]')
        const menu = this.elements.menu

        if (!groups || !main || !menu) {
            return
        }

        const afterAnchor = (groups.afterAnchor ?? []).filter(Boolean)
        const append = (groups.append ?? []).filter(Boolean)
        const nodes = [...afterAnchor, ...append]

        if (!nodes.length) {
            return
        }

        if (!this.navigationPlacements) {
            this.navigationPlacements = new Map()
        }

        nodes.forEach((node) => {
            if (!this.navigationPlacements.has(node)) {
                this.navigationPlacements.set(node, {
                    parent: node.parentNode,
                    before: node.nextSibling,
                })
            }
        })

        if (media('<iPadPortrait')) {
            let anchor = menu.children[0] ?? null

            afterAnchor.forEach((node) => {
                if (anchor) {
                    anchor.insertAdjacentElement('afterend', node)
                } else {
                    menu.appendChild(node)
                }

                anchor = node
            })

            append.forEach((node) => {
                menu.appendChild(node)
            })

            return
        }

        nodes.forEach((node) => {
            const home = this.navigationPlacements.get(node)

            if (!home) {
                return
            }

            home.parent.insertBefore(node, home.before)
        })
    }

    createActions(actions = this.elements.actions) {
        actions.forEach((entry) => {
            // Search
            if(entry.classList.contains('floating-actions')){
                let search = entry.querySelector('#floating-search-container')
                
                if(search){
                    search = {
                        container: search,
                        form: search.querySelector('form'),
                        input: search.querySelector('#floating-search-global'),
                        button: search.querySelector('button[type="submit"]'),
                    }
                    
                    search.button.onclick = (event) => {
                        if(!app.root.classList.contains('search-floating--open')){
                            event.preventDefault()
                            event.stopPropagation()
                            app.body.classList.add('search-floating--open')
                            search.input.focus()
                        } else {
                            app.body.classList.remove('search-floating--open')
                            search.input.blur()
                        }
                    }

                    search.input.onblur = (event) => {
                        search.input.value = ''
                        app.body.classList.remove('search-floating--open')
                    }
                }
            } else if(entry.classList.contains('actions')){
                
            } else {
                return
            }

            // Menu
            let menu = entry.querySelector('#floating-menu-toggler')
            if(menu){
                menu.onclick = (event) => {
                    
                    if(!app.root.classList.contains('search-floating--open')){
                        event.preventDefault()
                        event.stopPropagation()
                        navigation.isOpen = true
                        navigation.current = this.elements.menu
                        app.body.classList.add('navigation--open')
                    } else {
                        MenuManager.closeAll()
                        this.closeNavigation()
                        event.preventDefault()
                        event.stopPropagation()
                    }

                    app.body.classList.remove('search-floating--open')
                }
            }
        })
    }

    createLanguage(container = this.elements.language) {
        console.log(container)
        if (!container) return

        let language = {
            toggler: container.querySelector('#navigation-language-toggler'),
            select: container.querySelector('select.navigation-language--select'),
            menu: container.querySelector('ul.language-selector')
        }

        if (!language.toggler || !language.select || !language.menu) return

        const setOpen = (open) => {
            app.body.classList.toggle('navigation-language--open', open)
            language.toggler.setAttribute('aria-expanded', open ? 'true' : 'false')
        }

        language.toggler.onclick = (event) => {
            event.preventDefault()
            event.stopPropagation()
            app.body.classList.toggle('navigation-language--open')
        }

        language.select.onchange = (event) => {
            window.location.assign(language.select.value)
        }
    }

    createMega() {
        MenuManager.onAfterClose = () => this.setColor()
        MenuManager.onBeforeToggle = (entry, submenu) => {
            if (!submenu && media('>=iPadLandscape')) {
                this.closeNavigation()
            }
        }

        this.element.querySelectorAll('[data-menu-target]').forEach((entry) => {
            MenuManager.toggle(entry)
        })

        this.element.querySelectorAll('[data-submenu-target]').forEach((entry) => {
            MenuManager.toggle(entry, true)
        })

        this.element.querySelectorAll('[data-menu-close]').forEach((entry) => {
            MenuManager.close(entry)
        })

        this.element.querySelectorAll('[data-submenu-close]').forEach((entry) => {
            MenuManager.close(entry, true)
        })
    }

    addEventListeners() {
        window.addEventListener('resize', () => {
            app.root.style.setProperty('--window-height', `${window.innerHeight}px`)
            app.root.style.setProperty('--window-width', `${window.innerWidth}px`)
            app.root.style.setProperty('--navigation-height', `${app.nav.clientHeight}px`)
            app.root.style.setProperty('--brand-logo-height', `${this.elements.logo.clientHeight}px`)
            app.root.style.setProperty('--brand-logo-width', `${this.elements.logo.clientWidth}px`)

            if(app.nav.querySelector('.navbar-menu')) {
                app.root.style.setProperty('--navbar-menu-height', `${app.nav.querySelector('.navbar-menu').clientHeight}px`)
            }

            if(app.nav.querySelector('.statusbar')) {
                app.root.style.setProperty('--statusbar-height', `${app.nav.querySelector('.statusbar').clientHeight}px`)
            }

            if(navigation.isOpen){
                this.closeNavigation()
            }

            if (menu.isOpen || media('<iPadPortrait')) {
                MenuManager.closeAll()
            }

            this.setColor()
            this.syncQuickMenuPlacement(this.navigationMovableEntries())
        })

        window.visualViewport.addEventListener('resize', () => {
            app.root.style.setProperty('--viewport-height', `${window.visualViewport.height}px`)
            app.root.style.setProperty('--brand-logo-height', `${this.elements.logo.clientHeight}px`)
            app.root.style.setProperty('--brand-logo-width', `${this.elements.logo.clientWidth}px`)
        })

        if(!app.viewport){
            app.viewport = window
        }

        app.viewport.addEventListener('scroll', (event) => {
            if(app.nav){
                const currentScroll = this.getScrollTop()

                if (currentScroll >= app.nav.clientHeight) {
                    app.body.classList.add('navigation--scrolled')
                } else {
                    app.body.classList.remove('navigation--scrolled')
                }

                this.setColor()
            }

            if(app.nav && app.nav.querySelector('.menu-toggler')) {
                if(this.getScrollTop() > app.nav.clientHeight){
                    app.body.classList.add('navigation--floating')
                } else {
                    app.body.classList.remove('navigation--floating')
                }
            }
        })
    }
}

export class Footer extends Module {
    constructor() {
        super({
            element: 'footer', 
            elements: {
                language: '#footer-language-switcher',
            }
        })
    }

    create() {
        super.create()

        app.footer = this.element
        if(this.elements.language) { this.createLanguage() }
    }

    createLanguage(container = this.elements.language) {
        if (!container) return

        let language = {
            toggler: container.querySelector('#footer-language-toggler'),
            select: container.querySelector('select.footer-language--select'),
            menu: container.querySelector('ul.language-selector')
        }

        if (!language.toggler || !language.select || !language.menu) return

        const setOpen = (open) => {
            app.body.classList.toggle('footer-language--open', open)
            language.toggler.setAttribute('aria-expanded', open ? 'true' : 'false')
        }

        language.toggler.onclick = (event) => {
            event.preventDefault()
            event.stopPropagation()
            app.body.classList.toggle('footer-language--open')
        }

        language.select.onchange = (event) => {
            window.location.assign(language.select.value)
        }
    }
}
